/**
 * Frontend mirror of backend `_default_elements` and presets.
 *
 * Keeping the same coordinate formulae on both sides means a brand-new
 * preset opens in the designer at the exact pixel positions the backend
 * renderer will use.
 */

export const ELEMENT_KEYS = [
  "stock_code", "name", "material", "weight", "ring_size", "category", "branch", "barcode_legacy",
];
export const ALL_KEYS = [...ELEMENT_KEYS, "barcode"];

export const ELEMENT_LABEL_TR = {
  stock_code:     "Stok Kodu",
  name:           "Ürün Adı",
  material:       "Materyal / Ayar",
  weight:         "Ağırlık",
  ring_size:      "Yüzük Ölçüsü",
  category:       "Kategori",
  branch:         "Şube",
  barcode_legacy: "Legacy Barkod",
  barcode:        "Barkod (Grafik)",
};

export const SAMPLE_VALUES = {
  stock_code:     "UZG000001",
  name:           "Yüzük Pırlanta",
  material:       "22 Ayar Altın",
  weight:         "2.45 gr",
  ring_size:      "Ölçü 17",
  category:       "Yüzükler",
  branch:         "Merkez Şube",
  barcode_legacy: "8690000000001",
};

const round2 = (n) => Math.round(n * 100) / 100;

export function defaultElementsFor(width_mm, height_mm, margin_mm, show = {}) {
  const W = Number(width_mm) || 50;
  const H = Number(height_mm) || 25;
  const M = Number(margin_mm) ?? 1.5;

  const header_font = Math.max(8, Math.round(H * 0.42 * 10) / 10);
  const body_font   = Math.max(5, Math.round(H * 0.26 * 10) / 10);
  const small_font  = Math.max(4.5, Math.round(H * 0.20 * 10) / 10);

  const header_y    = M;
  // Wide-label optimisation (Phase 38): the barcode block uses full label
  // width — its internal quiet zones (white space INSIDE the python-barcode
  // PNG) already satisfy scanner spec, so subtracting the visual margin
  // here would shrink the bars unnecessarily on wide thermal labels.
  const bc_y        = M + H * 0.20;
  const bc_h        = Math.max(6, H * 0.40);
  const bc_w        = Math.max(8, W);
  const bc_x        = 0;
  const name_y      = M + H * 0.64;
  const material_y  = M + H * 0.78;
  const weight_x    = Math.max(M + W * 0.45, M + 15);
  const ring_x      = W - M;
  const legacy_y    = M + H * 0.92;

  return {
    stock_code:     { visible: show.stock_code ?? true,      x: round2(M),       y: round2(header_y),   font_size: header_font, bold: true,  uppercase: false, align: "left"  },
    barcode:        { visible: true,                          x: round2(bc_x),    y: round2(bc_y),       width_mm: round2(bc_w), height_mm: round2(bc_h) },
    name:           { visible: show.name ?? true,             x: round2(M),       y: round2(name_y),     font_size: body_font,   bold: true,  uppercase: false, align: "left"  },
    material:       { visible: show.material ?? true,         x: round2(M),       y: round2(material_y), font_size: small_font,  bold: false, uppercase: false, align: "left"  },
    weight:         { visible: show.weight ?? true,           x: round2(weight_x),y: round2(material_y), font_size: small_font,  bold: false, uppercase: false, align: "left"  },
    ring_size:      { visible: show.ring_size ?? true,        x: round2(ring_x),  y: round2(name_y),     font_size: body_font,   bold: false, uppercase: false, align: "right" },
    category:       { visible: show.category ?? false,        x: round2(M),       y: round2(legacy_y),   font_size: small_font,  bold: false, uppercase: false, align: "left"  },
    branch:         { visible: show.branch ?? false,          x: round2(W - M),   y: round2(legacy_y),   font_size: small_font,  bold: false, uppercase: false, align: "right" },
    barcode_legacy: { visible: show.barcode_legacy ?? false,  x: round2(M),       y: round2(legacy_y),   font_size: small_font,  bold: false, uppercase: false, align: "left"  },
  };
}

export const PRESETS = [
  { id: "ring",     name: "Yüzük Etiketi (Ring Tag)",       width_mm: 50, height_mm: 25, margin_mm: 1.5, show_ring: true  },
  { id: "necklace", name: "Kolye Etiketi (Necklace Tag)",   width_mm: 60, height_mm: 30, margin_mm: 2.0, show_ring: false },
  { id: "bracelet", name: "Bilezik Etiketi (Bracelet Tag)", width_mm: 55, height_mm: 28, margin_mm: 1.8, show_ring: false },
  { id: "earring",  name: "Küpe Etiketi (Earring Tag)",     width_mm: 40, height_mm: 22, margin_mm: 1.2, show_ring: false },
  { id: "custom",   name: "Özel Etiket (Custom)",           width_mm: 50, height_mm: 25, margin_mm: 1.5, show_ring: false },
];

export function blankProfileFromPreset(preset, makeDefault = false) {
  const show = {
    stock_code: true, name: true, material: true, weight: true,
    ring_size: !!preset.show_ring, category: false, branch: false, barcode_legacy: false,
  };
  return {
    name: preset.name,
    width_mm: preset.width_mm,
    height_mm: preset.height_mm,
    margin_mm: preset.margin_mm,
    barcode_type: "ean8",
    is_default: makeDefault,
    show_fields: show,
    elements: defaultElementsFor(preset.width_mm, preset.height_mm, preset.margin_mm, show),
  };
}

// Resolve the per-element sample text for the canvas (so the designer
// shows a realistic preview without making a backend round-trip).
export function sampleText(key, value) {
  if (value !== undefined) return value;
  return SAMPLE_VALUES[key] ?? "";
}
